import { Controller } from "@hotwired/stimulus"

/**
 * Bug Report Controller
 *
 * Opens a minimal modal for submitting bug reports: just a textarea and submit.
 * URL, module context, and user info are captured automatically.
 */
export default class extends Controller {
  open(event) {
    event?.preventDefault()
    if (document.getElementById("bug-report-modal")) return

    const overlay = document.createElement("div")
    overlay.id = "bug-report-modal"
    overlay.className = "fixed inset-0 z-[200] flex items-center justify-center bg-black/40 transition-opacity duration-200"
    overlay.innerHTML = `
      <div class="bg-white rounded-xl shadow-2xl border border-mod-border w-full max-w-md mx-4 overflow-hidden">
        <div class="flex items-center justify-between px-5 py-4 border-b border-mod-border bg-mod-warm-50">
          <div class="flex items-center gap-2">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-red-500"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <h3 class="text-sm font-semibold text-mod-charcoal">Report a Bug</h3>
          </div>
          <button id="bug-report-close" class="text-mod-warm-400 hover:text-mod-charcoal transition-colors">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <form id="bug-report-form" class="p-5 space-y-4">
          <div>
            <label class="block text-xs font-medium text-mod-text-secondary mb-1">What went wrong?</label>
            <textarea name="body" rows="5" required placeholder="Describe the issue you encountered…" class="w-full rounded-lg border border-mod-border px-3 py-2 text-sm focus:border-mod-charcoal focus:ring-1 focus:ring-mod-charcoal outline-none resize-y"></textarea>
          </div>
          <div class="flex items-center justify-between pt-1">
            <span class="text-xs text-mod-warm-400">Page URL captured automatically</span>
            <button type="submit" class="rounded-lg bg-mod-charcoal px-5 py-2 text-sm font-semibold text-white hover:bg-red-600 transition-colors">Submit</button>
          </div>
          <div id="bug-report-status" class="hidden text-center text-sm"></div>
        </form>
      </div>
    `

    document.body.appendChild(overlay)
    document.body.style.overflow = "hidden"

    // Focus textarea
    setTimeout(() => overlay.querySelector('textarea[name="body"]').focus(), 100)

    // Close handlers
    const close = () => {
      overlay.remove()
      document.body.style.overflow = ""
    }
    overlay.querySelector("#bug-report-close").addEventListener("click", close)
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close() })
    document.addEventListener("keydown", function handler(e) {
      if (e.key === "Escape") { close(); document.removeEventListener("keydown", handler) }
    })

    // Submit
    overlay.querySelector("#bug-report-form").addEventListener("submit", async (e) => {
      e.preventDefault()
      const form = e.target
      const status = overlay.querySelector("#bug-report-status")
      const submitBtn = form.querySelector('button[type="submit"]')

      submitBtn.disabled = true
      submitBtn.textContent = "Submitting…"

      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
        const res = await fetch("/feedback", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
            "Accept": "application/json"
          },
          body: JSON.stringify({
            type: "bug",
            body: form.body.value,
            url: window.location.href
          })
        })

        if (res.ok) {
          status.className = "text-center text-sm text-green-600 font-medium"
          status.textContent = "✓ Bug report submitted. Thank you!"
          status.classList.remove("hidden")
          setTimeout(close, 1500)
        } else {
          const err = await res.json().catch(() => ({}))
          throw new Error(err.error || `Server error (${res.status})`)
        }
      } catch (err) {
        status.className = "text-center text-sm text-red-600"
        status.textContent = `Error: ${err.message}`
        status.classList.remove("hidden")
        submitBtn.disabled = false
        submitBtn.textContent = "Submit"
      }
    })
  }
}
